// components/PaymentModal.tsx - Payment Modal Component
import React, { useState } from 'react';
import { X, CreditCard } from 'lucide-react';
import { Property, PaymentFrequency, PaymentMethod } from '../types';
import { calculateTotal, calculateAmountByFrequency } from '../utils';
import { paymentFrequencies, paymentMethods } from '../data';

interface PaymentModalProps {
  property: Property;
  isOpen: boolean;
  onClose: () => void;
  onPayment: (data: {
    amount: number;
    method: string;
    frequency: string;
  }) => void;
}

const PaymentModal: React.FC<PaymentModalProps> = ({ 
  property, 
  isOpen, 
  onClose, 
  onPayment 
}) => {
  const [paymentAmount, setPaymentAmount] = useState('');
  const [paymentMethod, setPaymentMethod] = useState('cash');
  const [paymentFrequency, setPaymentFrequency] = useState('monthly');

  if (!isOpen) return null;

  const quickPaymentOptions = paymentFrequencies.map(freq => ({
    ...freq,
    amount: calculateAmountByFrequency(property, freq.id)
  }));

  const handlePayment = () => {
    if (!paymentAmount) return;
    
    onPayment({
      amount: parseFloat(paymentAmount),
      method: paymentMethod,
      frequency: paymentFrequency
    });
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-end">
      <div className="bg-white w-full rounded-t-3xl p-6 max-h-[85vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-2xl font-bold text-gray-900">Collect Payment</h2>
          <button
            onClick={onClose}
            className="w-10 h-10 bg-gray-100 hover:bg-gray-200 rounded-2xl flex items-center justify-center transition-colors"
          >
            <X className="w-5 h-5 text-gray-600" />
          </button>
        </div>

        <div className="bg-gradient-to-r from-gray-50 to-gray-100 rounded-2xl p-4 mb-6">
          <h3 className="text-lg font-bold text-gray-900 mb-2">{property.name}</h3>
          <p className="text-sm text-gray-600 mb-1">Plot {property.plotNo} • Road {property.roadNo}</p>
          <p className="text-xs text-gray-500">{property.phone}</p>
        </div>

        <div className="mb-6">
          <h4 className="text-lg font-semibold text-gray-900 mb-4">Payment Frequency</h4>
          <div className="grid grid-cols-1 gap-3">
            {quickPaymentOptions.map(option => (
              <button
                key={option.id}
                onClick={() => {
                  setPaymentFrequency(option.id);
                  setPaymentAmount(option.amount.toString());
                }}
                className={`p-4 rounded-2xl border-2 transition-all duration-200 ${
                  paymentFrequency === option.id
                    ? 'border-green-500 bg-green-50'
                    : 'border-gray-200 bg-white hover:border-gray-300'
                }`}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <span className="text-2xl">{option.icon}</span>
                    <div className="text-left">
                      <div className="font-semibold text-gray-900">{option.name}</div>
                      <div className="text-sm text-gray-500">
                        {option.multiplier} month{option.multiplier > 1 ? 's' : ''}
                        {option.discount && ` • ${option.discount}% discount`}
                      </div>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-xl font-bold text-green-600">₹{option.amount}</div>
                    {option.discount && (
                      <div className="text-xs text-green-500">
                        Save ₹{(property.monthlyRate * option.multiplier * option.discount / 100)}
                      </div>
                    )}
                  </div>
                </div>
              </button>
            ))}
          </div>
        </div>

        <div className="bg-red-50 rounded-2xl p-4 mb-6 border border-red-200">
          <h4 className="font-semibold text-red-900 mb-3">Outstanding Amount</h4>
          <div className="grid grid-cols-3 gap-3 mb-3">
            <div className="text-center">
              <div className="text-xs text-gray-500">Base</div>
              <div className="font-bold text-gray-900">₹{property.monthlyRate}</div>
            </div>
            <div className="text-center">
              <div className="text-xs text-gray-500">Dues</div>
              <div className="font-bold text-yellow-600">₹{property.dues}</div>
            </div>
            <div className="text-center">
              <div className="text-xs text-gray-500">Penalty</div>
              <div className="font-bold text-red-600">₹{property.penalty}</div>
            </div>
          </div>
          <div className="bg-white p-3 rounded-xl border border-red-200">
            <div className="text-center">
              <div className="text-sm text-gray-600">Total Outstanding</div>
              <div className="text-2xl font-bold text-red-600">₹{calculateTotal(property)}</div>
            </div>
          </div>
        </div>

        <div className="mb-6">
          <label className="block text-lg font-semibold text-gray-900 mb-3">Payment Amount</label>
          <input
            type="number"
            value={paymentAmount}
            onChange={(e) => setPaymentAmount(e.target.value)}
            placeholder="Enter custom amount"
            className="w-full p-4 border-2 border-gray-200 rounded-2xl text-2xl font-bold text-center focus:ring-2 focus:ring-green-500 focus:border-green-500 bg-white"
          />
          <button
            onClick={() => setPaymentAmount(calculateTotal(property).toString())}
            className="w-full mt-3 py-3 text-sm text-green-600 border-2 border-green-200 rounded-xl hover:bg-green-50 font-medium transition-colors"
          >
            Pay Full Outstanding (₹{calculateTotal(property)})
          </button>
        </div>

        <div className="mb-6">
          <h4 className="text-lg font-semibold text-gray-900 mb-3">Payment Method</h4>
          <div className="grid grid-cols-2 gap-3">
            {paymentMethods.map(method => (
              <button
                key={method.id}
                onClick={() => setPaymentMethod(method.id)}
                className={`p-4 rounded-2xl border-2 transition-all duration-200 ${
                  paymentMethod === method.id
                    ? 'border-green-500 bg-green-50'
                    : 'border-gray-200 bg-white hover:border-gray-300'
                }`}
              >
                <div className="text-center">
                  <div className="text-2xl mb-2">{method.icon}</div>
                  <div className="font-medium text-gray-900">{method.name}</div>
                </div>
              </button>
            ))}
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <button
            onClick={onClose}
            className="py-4 px-6 border-2 border-gray-200 text-gray-700 rounded-2xl font-semibold text-lg hover:bg-gray-50 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handlePayment}
            disabled={!paymentAmount}
            className="py-4 px-6 bg-gradient-to-r from-green-500 to-green-600 text-white rounded-2xl font-semibold text-lg disabled:from-gray-300 disabled:to-gray-400 disabled:cursor-not-allowed hover:from-green-600 hover:to-green-700 transition-all duration-200 shadow-lg"
          >
            Process Payment
          </button>
        </div>
      </div>
    </div>
  );
};

export default PaymentModal;